import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Serving } from '../../shared/foods';

interface GramsInputProps {
  value: number;
  onChange: (grams: number) => void;
  servings?: Serving[];
  liquid?: boolean;
  label?: string;
}

function step(g: number): number {
  if (g < 30) return 5;
  if (g < 300) return 10;
  return 25;
}

/** Grams are the base unit: a stepper plus one-tap serving presets. */
export function GramsInput({ value, onChange, servings = [], liquid, label = 'Amount' }: GramsInputProps) {
  const [text, setText] = useState(String(Math.round(value)));
  useEffect(() => {
    setText(String(Math.round(value * 10) / 10));
  }, [value]);

  const commit = (raw: string) => {
    const n = Number(raw.replace(',', '.'));
    if (Number.isFinite(n) && n > 0) onChange(Math.min(5000, Math.round(n * 10) / 10));
    else setText(String(value));
  };

  const presets = servings.slice(0, 4);
  return (
    <div className="stack" style={{ '--gap': '8px' } as React.CSSProperties}>
      <div className="row wrap">
        <div className="grams" role="group" aria-label={label}>
          <button type="button" className="grams__btn" aria-label="Less" onClick={() => onChange(Math.max(1, value - step(value - 1)))}>
            <Minus size={16} />
          </button>
          <input
            className="grams__input num"
            inputMode="decimal"
            aria-label={`${label} in ${liquid ? 'milliliters' : 'grams'}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
            }}
          />
          <span className="grams__unit">{liquid ? 'ml' : 'g'}</span>
          <button type="button" className="grams__btn" aria-label="More" onClick={() => onChange(Math.min(5000, value + step(value)))}>
            <Plus size={16} />
          </button>
        </div>
        {presets.length > 0 && (
          <div className="servings">
            {presets.map((s) => (
              <button
                type="button"
                key={`${s.label}-${s.grams}`}
                className={`chip ${Math.abs(s.grams - value) < 0.5 ? 'is-active' : ''}`}
                onClick={() => onChange(s.grams)}
                title={`${s.grams} g`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
